import React from 'react';
import { useGamePurchases } from '../../hooks/useGamePurchases';
import { useMultipliers } from '../../hooks/useMultipliers';
import { TrendingUp, Coins, MousePointer2 } from 'lucide-react';

interface GameStatsProps {
  clicks: number;
  coinsPerClick: number;
  balance: number;
  autoClickerCount: number;
  walletAddress: string | null;
}

export function GameStats({ 
  clicks, 
  coinsPerClick,
  balance,
  autoClickerCount,
  walletAddress
}: GameStatsProps) {
  const { calculateTotalBonus } = useGamePurchases(walletAddress);
  const { calculateTotalMultiplier } = useMultipliers(walletAddress);
  
  // Calculate total multiplier
  const totalBonus = calculateTotalBonus();
  const multiplier = calculateTotalMultiplier();
  
  // Calculate effective coins per click
  const effectiveCoinsPerClick = Math.floor((coinsPerClick + totalBonus) * multiplier);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
      <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-lg p-6 backdrop-blur-sm border border-blue-500/20">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp className="text-blue-400" size={24} />
          <h3 className="text-lg text-blue-100">Total Clicks</h3>
        </div>
        <p className="text-3xl font-bold text-blue-100">{clicks.toLocaleString()}</p>
        <p className="text-sm text-blue-300 mt-2">Total clicks</p>
      </div>

      <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 rounded-lg p-6 backdrop-blur-sm border border-yellow-500/20">
        <div className="flex items-center gap-3 mb-2">
          <Coins className="text-yellow-400" size={24} />
          <h3 className="text-lg text-yellow-100">Tokens</h3>
        </div>
        <p className="text-3xl font-bold text-yellow-100">
          {balance.toLocaleString()} <span className="text-lg">TOKENS</span>
        </p>
        <p className="text-sm text-yellow-300 mt-2">
          {effectiveCoinsPerClick.toLocaleString()} tokens per click (x{multiplier.toFixed(1)})
        </p>
      </div>

      <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-lg p-6 backdrop-blur-sm border border-purple-500/20">
        <div className="flex items-center gap-3 mb-2">
          <MousePointer2 className="text-purple-400" size={24} />
          <h3 className="text-lg text-purple-100">Auto-Clickers</h3>
        </div>
        <p className="text-3xl font-bold text-purple-100">
          {autoClickerCount} <span className="text-lg">active</span>
        </p>
        <p className="text-sm text-purple-300 mt-2">
          {(autoClickerCount * effectiveCoinsPerClick).toLocaleString()} tokens/sec
        </p>
      </div>
    </div>
  );
}